import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { renderStateName } from '@/screens/scan/states'
import { normalizeLabel } from '@/screens/scan/lib/labels'
import type { ChecklistItem } from '@/screens/scan/types'
import { DetailRow } from './DetailRow'

/**
 * Picker row: checkbox to select, a two-line title/description body that
 * expands on click to reveal the full checklist-item details (mirrors the
 * ramp-web-app scanner card), and right-aligned type/scope chips.
 */
export function SelectItem({
  item,
  checked,
  onToggle
}: {
  item: ChecklistItem
  checked: boolean
  onToggle: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  // Mirror ramp-web-app's parsing: dedupe states and resolve to full names.
  const uniqueStates = item.states?.length ? [...new Set(item.states.filter(Boolean))] : []
  const states = uniqueStates.length ? uniqueStates.map(renderStateName).join(', ') : undefined
  const stateLabel = uniqueStates.length
    ? uniqueStates.length === 1
      ? renderStateName(uniqueStates[0])
      : `${uniqueStates.length} states`
    : undefined
  // Type + scope share one line so the column stays at two rows (name + desc).
  const typeScope = [item.type, item.scope]
    .filter(Boolean)
    .map((value) => normalizeLabel(value as string))
    .join(' · ')
  const typeOfInsurance = item.typeOfInsurances?.length ? item.typeOfInsurances.join(', ') : undefined
  const subTypeOfInsurance = item.subTypeOfInsurances?.length ? item.subTypeOfInsurances.join(', ') : undefined
  const hasDetails = Boolean(
    item.description ||
    item.type ||
    item.scope ||
    item.status ||
    item.topic ||
    item.statutes ||
    states ||
    typeOfInsurance ||
    subTypeOfInsurance
  )

  return (
    <div className={`ramp-scan-item${checked ? ' is-selected' : ''}${expanded ? ' is-expanded' : ''}`}>
      <div className='ramp-scan-item-row'>
        <input type='checkbox' checked={checked} onChange={onToggle} aria-label={`Select ${item.name}`} />
        <button
          type='button'
          className='ramp-scan-item-body'
          disabled={!hasDetails}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className='ramp-scan-item-main'>
            <span className='ramp-scan-item-name'>{item.name}</span>
            {item.description ? (
              <span className='ramp-scan-item-desc'>{item.description}</span>
            ) : item.topic ? (
              <span className='ramp-scan-item-desc'>{item.topic}</span>
            ) : null}
          </span>
          <span className='ramp-scan-item-side'>
            <span className='ramp-scan-item-tags'>
              {typeScope ? <span className='ramp-scan-tag'>{typeScope}</span> : null}
              {stateLabel ? <span className='ramp-scan-tag'>{stateLabel}</span> : null}
            </span>
            {hasDetails ? <ChevronDown size={14} className='ramp-scan-item-chevron' /> : null}
          </span>
        </button>
      </div>

      {expanded && hasDetails ? (
        <div className='ramp-scan-item-details'>
          <DetailRow label='Description' value={item.description} />
          <DetailRow label='Type' value={item.type ? normalizeLabel(item.type) : undefined} />
          <DetailRow label='Scope' value={item.scope ? normalizeLabel(item.scope) : undefined} />
          <DetailRow label='Status' value={item.status ? normalizeLabel(item.status) : undefined} />
          <DetailRow label='Topic' value={item.topic} />
          <DetailRow label='Source' value={item.statutes ? normalizeLabel(item.statutes) : undefined} />
          <DetailRow label='States' value={states} />
          <DetailRow label='Type of Insurance' value={typeOfInsurance} />
          <DetailRow label='Sub-Type of Insurance' value={subTypeOfInsurance} />
        </div>
      ) : null}
    </div>
  )
}
